import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {VStack, useDisclosure} from '@chakra-ui/react';
import {
  HaliRenderOne,
  useCommand,
  useHalState,
  useHalStates,
} from '@synako/haligator';
import {Resource, State} from '@synako/halitrek';
import {ItemCatalogue2StateFF, isItemStateCatalogueFF} from 'ff-common';
import {
  ArticleDesc,
  ItemListeSelectionDesc,
  LigneDePanierDesc,
  ListeDesc,
} from 'api-types/ffconnect-hal';
import {TriSelection} from 'api-types/ffconnect';
import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {DeleteButton} from '@/domains/ligneProduit/DeleteButton';
import {usePanier} from 'ff-common';
import {PrixBlockUnitaire} from '@/domains/cadencier/components/PrixBlockUnitaire';
import {ArticleBlock} from '@/domains/panier/PanierFound/ArticleBlock';
import {useBuyingBlocks} from '@/hooks/panier/useBuyingBlocks';
import {useAjouterAvecPrixSaisi} from '@/hooks/panier/useAjouterAvecPrixSaisi';
import React from 'react';
import {LigneProduitCommune} from '@/domains/ligneProduit/LigneProduitCommune';
import {SectionHeader} from '@/domains/ligneProduit/SectionHeader';
import {useClient} from '@/haligator-factory';
import {usePrixCategorieTag} from '@/hooks/usePrixCategorieTag';
import {useEditionPrixState} from '@/hooks/panier/usePriceEditor';

interface ListeSectionProps {
  produits: Resource<ItemListeSelectionDesc>[];
  liste: State<ListeDesc> | undefined;
  tri_selection?: TriSelection;
}
/**
 * Affiche les produits d'une liste, groupés par catégorie web si tri_selection est disponible.
 */
export const ListeSection: React.FC<ListeSectionProps> = ({
  produits,
  liste: selectedListState,
  tri_selection,
}) => {
  const {states: lignesDePanier} = useHalStates(
    usePanier().followAll('lignes_de_panier'),
  );

  // Si tri_selection disponible, grouper les produits par catégorie
  if (tri_selection && tri_selection.length > 0) {
    return (
      <VStack
        alignItems='stretch'
        width='100%'
        gap='0'
      >
        {tri_selection.map(({categorie_id, libelle_web, articles}) => {
          if (articles.length === 0) return null;
          return (
            <ListeCategorieSection
              key={categorie_id}
              id={`liste_cat_${categorie_id}`}
              title={libelle_web}
              articles={articles}
              listState={selectedListState}
              lignesDePanier={lignesDePanier}
            />
          );
        })}
      </VStack>
    );
  }

  // Fallback : affichage plat sans catégories
  return (
    <VStack
      alignItems='stretch'
      width='100%'
      mt='3'
    >
      <VStack gap='0'>
        {produits.map((p: Resource<ItemListeSelectionDesc>) => {
          return (
            <LoadLigneListe
              key={`ligne_liste_${p.uri}`}
              produit={p}
              listState={selectedListState}
              lignesDePanier={lignesDePanier}
            />
          );
        })}
      </VStack>
    </VStack>
  );
};

// Section catégorie avec header collapsible
const ListeCategorieSection: React.FC<{
  id: string;
  title: string;
  articles: TriSelection[number]['articles'];
  listState: State<ListeDesc> | undefined;
  lignesDePanier: State<LigneDePanierDesc>[] | undefined;
}> = React.memo(({id, title, articles, listState, lignesDePanier}) => {
  const {isOpen, onToggle} = useDisclosure();
  const clientNav = useClient();

  return (
    <SectionHeader
      title={title}
      count={articles.length}
      id={id}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <VStack
        gap='0'
        px='12px'
      >
        {articles.map(({code: code_article}) => (
          <HaliRenderOne
            key={`ligne_liste_${code_article}`}
            nav={clientNav.follow('go:item_catalogue_2', {
              marketplace_id: 'FF',
              code: code_article,
            })}
            render={({state: itemState}) => {
              if (!isItemStateCatalogueFF(itemState)) return null;
              const ligneDePanier = lignesDePanier?.find(
                (l) => l.data.code_article === code_article,
              );
              return (
                <LigneListe
                  code_article={code_article}
                  itemState={itemState}
                  listState={listState}
                  ligneDePanier={ligneDePanier}
                />
              );
            }}
          />
        ))}
      </VStack>
    </SectionHeader>
  );
});

// Version utilisée avec tri_selection
const LigneListe: React.FC<{
  code_article: string;
  itemState: ItemCatalogue2StateFF;
  listState: State<ListeDesc> | undefined;
  ligneDePanier: State<LigneDePanierDesc> | undefined;
}> = React.memo(({code_article, itemState, listState, ligneDePanier}) => {
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const {state: articleState, loading: articleLoading} = useHalState(
    itemState.follow('article'),
  );

  // Commande de suppression de l'article de la liste
  const {submit, isSubmitting, isDisabled} = useCommand(
    listState,
    'c:supprimer-article-d-une-liste',
    {
      onError,
      onSuccess: () => {
        textToast(t('toast.supprime_liste', {code_article}));
      },
    },
  );

  const deleteAction = useCallback(() => {
    if (!listState) return;
    submit({liste_id: listState.data.id, code_article});
  }, [listState, submit, code_article]);

  if (articleLoading || !articleState) return null;

  return (
    <ProduitListe
      itemState={itemState}
      articleState={articleState}
      prixHT={itemState.data.prix_unitaire_ht}
      prixTtc={itemState.data.prix_unitaire_ttc}
      tauxTVA={itemState.data.tva}
      deleteAction={deleteAction}
      somethingSubmitting={isSubmitting}
      deleteEnCours={isSubmitting || isDisabled}
      lignePanier={ligneDePanier}
    />
  );
});

// Version originale utilisée en fallback (sans tri_selection)
const LoadLigneListe: React.FC<{
  produit: Resource<ItemListeSelectionDesc>;
  listState: State<ListeDesc> | undefined;
  lignesDePanier: State<LigneDePanierDesc>[] | undefined;
}> = React.memo(({produit, listState, lignesDePanier}) => {
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const {state: articleState, loading: articleLoading} = useHalState(
    produit.follow('self').follow('article'),
  );

  const {submit, isSubmitting, isDisabled} = useCommand(
    listState,
    'c:supprimer-article-d-une-liste',
    {
      onError,
      onSuccess: () => {
        if (!articleState) return;
        const code_article = articleState.data.tolede.code_article;
        textToast(t('toast.supprime_liste', {code_article}));
      },
    },
  );

  const deleteAction = useCallback(() => {
    if (!articleState) {
      textToast(t('toast.produit_introuvable_supprime'));
      return;
    }
    const code_article = articleState.data.tolede.code_article;
    if (!listState) {
      return;
    }
    submit({liste_id: listState.data.id, code_article});
  }, [articleState, textToast, t, listState, submit]);

  if (articleLoading) {
    return 'Chargement...';
  }

  return (
    <>
      <HaliRenderOne
        nav={produit.follow('item_catalogue_2')}
        render={({state: itemState}) => {
          if (!articleState) {
            return (
              // eslint-disable-next-line i18next/no-literal-string
              <div>
                Article dans le cadencier mais pas dans la base FF{' '}
                {itemState.data.code}
              </div>
            );
          }
          // Pour le typage
          if (!isItemStateCatalogueFF(itemState)) return null;
          return (
            <ProduitListe
              itemState={itemState}
              articleState={articleState}
              prixHT={itemState.data.prix_unitaire_ht}
              prixTtc={itemState.data.prix_unitaire_ttc}
              tauxTVA={itemState.data.tva}
              deleteAction={deleteAction}
              somethingSubmitting={isSubmitting}
              deleteEnCours={isSubmitting || isDisabled}
              lignePanier={lignesDePanier?.find(
                (l) => l.data.code_article === itemState.data.code,
              )}
            />
          );
        }}
      />
    </>
  );
});

const ProduitListe: React.FC<{
  itemState: ItemCatalogue2StateFF;
  articleState: State<ArticleDesc>;
  prixHT: number;
  prixTtc?: number;
  tauxTVA?: number;
  somethingSubmitting: boolean;
  deleteEnCours?: boolean;
  deleteAction?: () => void;
  lignePanier: State<LigneDePanierDesc> | undefined;
}> = React.memo(
  ({
    itemState,
    articleState,
    prixHT,
    prixTtc,
    tauxTVA,
    somethingSubmitting,
    deleteEnCours,
    deleteAction,
    lignePanier,
  }) => {
    const precommande = itemState.data.tolede.precommande;
    // Hors panier : la catégorie vient du catalogue, pas de la ligne. Celle-ci
    // n'est passée que pour une info : un prix saisi ou bloqué par le commercial,
    // qui prend alors la place du tag de catégorie.
    const {categorieDeTarif, modeTarifaireSouhaite} = usePrixCategorieTag(
      lignePanier,
      itemState.data.tolede.prix_par_livraison?.[0]?.categorie,
      {contexte: 'catalogue'},
    );
    const typePrix = itemState.data.tolede.prix_par_livraison?.[0]?.type;
    // Même règle que le bloc prix : promo et marché décalent la ligne vers le haut.
    const hasTag =
      !!(categorieDeTarif ?? modeTarifaireSouhaite) ||
      typePrix === 'promo' ||
      typePrix === 'marche';
    const textToast = useSimpleTextToast();
    const {uniteQuantiteBlock, buyBlock, commandeEnColis} = useBuyingBlocks(
      textToast,
      'liste',
      itemState,
      lignePanier,
    );
    const {
      ajouterAvecPrixSaisi,
      isAddingAvecPrix,
      saisieAvantAjout,
      codeArticle,
    } = useAjouterAvecPrixSaisi(itemState, commandeEnColis);
    const {prixDraft, onEditionPrixChange} = useEditionPrixState();
    const prixBlock = (
      <PrixBlockUnitaire
        prixUnitaire={prixHT}
        prixUnitaireTtc={prixTtc}
        uniteFacturation={itemState.data.unite_de_facturation}
        tauxTVA={tauxTVA}
        style={{h: '100%', mt: {base: '0', md: hasTag ? '-12px' : '15px'}}}
        typePrix={typePrix}
        categorieDeTarif={categorieDeTarif}
        modeTarifaireSouhaite={modeTarifaireSouhaite}
        ligneDePanier={lignePanier}
        saisieAvantAjout={saisieAvantAjout}
        ajouterAvecPrixSaisi={ajouterAvecPrixSaisi}
        codeArticle={codeArticle}
        onEditionPrixChange={onEditionPrixChange}
      />
    );

    const deleteBlock = (
      <DeleteButton
        onClick={() => {
          deleteAction?.();
        }}
        isDisabled={!!deleteEnCours || !deleteAction}
        isLoading={somethingSubmitting}
      />
    );

    const articleBlock = (
      <ArticleBlock
        code_article={articleState.data.tolede.code_article}
        libelleFallback={lignePanier?.data.libelle}
        libelles_tronques
        ligneDePanier={lignePanier}
        afficherTagCadencier
        prixDraft={prixDraft}
      />
    );

    return (
      <LigneProduitCommune
        itemState={itemState}
        articleBlock={articleBlock}
        prixBlock={prixBlock}
        uniteQuantiteBlock={uniteQuantiteBlock}
        aPeser={articleState.data.tolede.a_peser}
        buyBlock={buyBlock}
        somethingSubmitting={somethingSubmitting || isAddingAvecPrix}
        deleteBlock={deleteBlock}
        precommande={precommande}
        ligneDePanier={lignePanier}
        afficher_suggestions
      />
    );
  },
);
